// ─────────────────────────────────────────────────────────────────────────────
// Equipment API — Comunicação com os endpoints de equipamento do backend.
//
// Funções:
// - equipItem:   Equipa um item da mochila no slot correspondente
// - unequipItem: Remove um item do slot e devolve à mochila
// - useItem:     Usa um item consumível (poção, pergaminho, etc.)
// ─────────────────────────────────────────────────────────────────────────────

/** Tipo de resposta genérica retornada pelos endpoints de equipamento */
export interface EquipmentResponse {
  success: boolean;
  equipment: Record<string, string>;
  inventory: Array<{ item_id: string; quantity: number; current_durability?: number }>;
  applied_effects?: Array<{
    item_name: string;
    attribute: string;
    value: number;
    expires_at: string;
    duration_minutes: number;
  }>;
  message?: string;
  error?: string;
}

/**
 * Helper genérico para requisições autenticadas.
 * Envia o token JWT no header Authorization como Bearer.
 */
async function req<T>(token: string, endpoint: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(`/api${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    },
  });

  if (!res.ok) {
    let errorMsg = `HTTP ${res.status}`;
    try {
      const errData = (await res.json()) as { error?: string };
      if (errData.error) errorMsg = errData.error;
    } catch {
      // Ignora erro de parse
    }
    throw new Error(errorMsg);
  }

  return (await res.json()) as T;
}

/**
 * API de equipamentos do jogador.
 * Todas as funções requerem autenticação via token JWT.
 */
export const equipmentApi = {
  /**
   * Equipa um item da mochila no slot correspondente.
   * Se já houver um item no slot, ele é automaticamente devolvido à mochila (swap).
   *
   * @param token   Token JWT do jogador
   * @param itemId  ID do item a equipar (deve estar na mochila)
   */
  equip: (token: string, itemId: string) =>
    req<EquipmentResponse>(token, '/user/equipment/equip', {
      method: 'POST',
      body: JSON.stringify({ item_id: itemId }),
    }),

  /**
   * Remove um item de um slot de equipamento e devolve à mochila.
   *
   * @param token Token JWT do jogador
   * @param slot  Slot a desequipar (Head, Body, Legs, Right_Hand, Left_Hand)
   */
  unequip: (token: string, slot: string) =>
    req<EquipmentResponse>(token, '/user/equipment/unequip', {
      method: 'POST',
      body: JSON.stringify({ slot }),
    }),

  /**
   * Usa um item consumível da mochila (ex: poção de vida).
   * O item é destruído e seus efeitos temporários são aplicados.
   *
   * @param token  Token JWT do jogador
   * @param itemId ID do item consumível a usar
   */
  useItem: (token: string, itemId: string) =>
    req<EquipmentResponse>(token, '/user/equipment/use', {
      method: 'POST',
      body: JSON.stringify({ item_id: itemId }),
    }),
};
